(() => {
  window.dataLayer = window.dataLayer || [];

  // ─── Utilities ───────────────────────────────────────────────────────────────

  let _cachedCart = null;

  // res.ok check prevents silent crash on 4xx / 5xx cart responses
  const fetchCart = async () => {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error(`cart ${res.status}`);
    const cart = await res.json();
    _cachedCart = cart;
    return cart;
  };

  // defer scripts run before DOMContentLoaded per spec, but guard anyway
  const onDOMReady = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const onIdle = (fn) =>
    'requestIdleCallback' in window
      ? requestIdleCallback(fn, { timeout: 2000 })
      : setTimeout(fn, 200);

  const buildItems = (items = []) =>
    items.map((item, index) => ({
      item_id:       item.sku || String(item.variant_id),
      item_name:     item.product_title || item.title,
      item_brand:    item.vendor        ?? '',
      item_category: item.product_type  ?? '',
      item_variant:  item.variant_title ?? '',
      price:         item.price / 100,
      quantity:      item.quantity,
      index,
    }));

  const getCoupon = (cart) => {
    const apps = cart?.cart_level_discount_applications;
    return apps?.length ? (apps[0].title ?? '') : '';
  };

  // GA4 requires clearing ecommerce before every ecommerce event
  const dlPushEcom = (event) => {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  };

  // ─── view_cart ────────────────────────────────────────────────────────────────

  let _lastFiredToken = null;

  const fireViewCart = () =>
    fetchCart()
      .then((cart) => {
        if (cart.token === _lastFiredToken) return;
        _lastFiredToken = cart.token;
        dlPushEcom({
          event: 'view_cart',
          ecommerce: {
            currency:  cart.currency,
            value:     cart.total_price / 100,
            num_items: cart.item_count,
            coupon:    getCoupon(cart),
            items:     buildItems(cart.items),
          },
        });
      })
      .catch(() => {});

  if (window.location.pathname === '/cart') onIdle(fireViewCart);

  // Seed cache on every page so remove_from_cart diff always has a baseline
  onIdle(() => { if (!_cachedCart) fetchCart().catch(() => {}); });

  // Cart drawer — fire only on user click, not programmatic open after add-to-cart
  onDOMReady(() => {
    const detailsEl = document.querySelector('cart-drawer details');
    const summaryEl = detailsEl?.querySelector('summary');
    if (!summaryEl) return;

    summaryEl.addEventListener('click', () => {
      if (!detailsEl.hasAttribute('open')) {
        _lastFiredToken = null;
        onIdle(fireViewCart);
      }
    });
  });

  // ─── remove_from_cart ─────────────────────────────────────────────────────────

  document.addEventListener('cart:updated', (e) => {
    let newCart;
    try {
      const raw = e?.detail?.cart;
      if (!raw) return;
      newCart = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(newCart?.items)) return;
    } catch { return; }

    if (!_cachedCart) { _cachedCart = newCart; return; }

    const oldCart = _cachedCart;
    _cachedCart = newCart;

    // Map for O(1) lookup instead of O(n) Array.find() per item
    const newItemMap = new Map(newCart.items.map((item) => [item.key, item]));

    oldCart.items?.forEach((oldItem, i) => {
      const removedQty = oldItem.quantity - (newItemMap.get(oldItem.key)?.quantity ?? 0);
      if (removedQty <= 0) return;

      dlPushEcom({
        event: 'remove_from_cart',
        ecommerce: {
          currency: newCart.currency,
          value:    (oldItem.price * removedQty) / 100,
          items: [{
            item_id:       oldItem.sku || String(oldItem.variant_id),
            item_name:     oldItem.product_title || oldItem.title,
            item_brand:    oldItem.vendor        ?? '',
            item_category: oldItem.product_type  ?? '',
            item_variant:  oldItem.variant_title ?? '',
            price:         oldItem.price / 100,
            quantity:      removedQty,
            index:         i,
          }],
        },
      });
    });
  });

  // ─── content_view ─────────────────────────────────────────────────────────────

  {
    const match = window.location.pathname.match(/^\/blogs\/([^/]+)\/[^/]+/);
    if (match) {
      const blogHandle = match[1];
      const TYPE_MAP = {
        'care-guide': 'care_guide',
        'care_guide': 'care_guide',
        'videos':     'video',
        'video':      'video',
        'faq':        'faq',
        'blog':       'blog',
      };

      onIdle(() => {
        window.dataLayer.push({
          event:            'content_view',
          content_type:     TYPE_MAP[blogHandle] ?? 'blog',
          content_title:    document.querySelector('meta[property="og:title"]')
                              ?.getAttribute('content') ?? document.title,
          content_category: blogHandle
                              .replace(/-/g, ' ')
                              .replace(/\b\w/g, (c) => c.toUpperCase()),
        });
      });
    }
  }

  // ─── filter_applied & sort_applied ───────────────────────────────────────────

  // Cached once — URL never changes on a collection page
  const _itemListName = (() => {
    const match = window.location.pathname.match(/^\/collections\/([^/]+)/);
    return match
      ? match[1].replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : document.title.split('|')[0].trim();
  })();

  const getProductCount = () =>
    document.getElementById('product-grid')?.querySelectorAll('li').length ?? 0;

  // Parses Shopify filter param_name — structured & SVG-safe
  // Format: filter.v.option.{Attr} | filter.v.availability | filter.v.price.gte | filter.p.tag
  const parseFilterType = (name = '') => {
    const parts = name.split('.');
    if (parts.includes('price'))        return 'price';
    if (parts.includes('availability')) return 'availability';
    if (parts.includes('tag'))          return 'tag';
    const optIdx = parts.indexOf('option');
    if (optIdx !== -1 && parts[optIdx + 1]) {
      return parts[optIdx + 1].toLowerCase().replace(/[\s-]+/g, '_');
    }
    return parts[parts.length - 1].toLowerCase().replace(/[\s-]+/g, '_') || 'unknown';
  };

  const SORT_TYPE_MAP = {
    'price-ascending':    'price_low_to_high',
    'price-descending':   'price_high_to_low',
    'created-descending': 'newest',
    'created-ascending':  'oldest',
    'best-selling':       'bestseller',
    'title-ascending':    'title_a_z',
    'title-descending':   'title_z_a',
    'manual':             'relevance',
  };

  const FACET_FORMS = '#FacetFiltersForm, #FacetSortFiltersForm, #FacetFiltersFormMobile';

  let _filterObserver = null;
  let _filterTimer    = null;
  let _priceDebounce  = null;

  const fireFilterAfterRender = (pending) => {
    // Disconnect any previous observer and clear its safety timer
    _filterObserver?.disconnect();
    clearTimeout(_filterTimer);

    const gridContainer = document.getElementById('ProductGridContainer')
      ?? document.getElementById('ajax-product-grid');

    const pushFilter = () => window.dataLayer.push({
      event: 'filter_applied',
      ...pending,
      results_count_after: getProductCount(),
    });

    if (!gridContainer) { pushFilter(); return; }

    // Safety fallback — if grid never mutates, clean up after 5s
    _filterTimer = setTimeout(() => {
      _filterObserver?.disconnect();
      _filterObserver = null;
    }, 5000);

    _filterObserver = new MutationObserver((_, obs) => {
      obs.disconnect();
      _filterObserver = null;
      clearTimeout(_filterTimer);
      pushFilter();
    });

    _filterObserver.observe(gridContainer, { childList: true, subtree: true });
  };

  // Single change listener handles checkbox filters + sort
  document.addEventListener('change', (e) => {
    const input = e.target;
    const { name = '', type, id, value, checked } = input;

    const form = input.closest(FACET_FORMS);
    if (!form) return;

    // ── sort ──
    if (name === 'sort_by') {
      window.dataLayer.push({
        event:          'sort_applied',
        sort_type:      SORT_TYPE_MAP[value] ?? value.replace(/-/g, '_'),
        item_list_name: _itemListName,
        results_count:  getProductCount(),
      });
      return;
    }

    if (type === 'hidden') return;
    if (type === 'checkbox' && !checked) return;
    if (type === 'number') return; // price handled via 'input' event

    // Label scoped to form first, document as fallback
    const labelEl = (type === 'checkbox' || type === 'radio') && id
      ? (form.querySelector(`label[for="${id}"]`) ?? document.querySelector(`label[for="${id}"]`))
      : null;

    const filterValue = labelEl
      ? labelEl.textContent.trim().replace(/\s*\(\d+\)\s*$/, '').trim()
      : value;

    fireFilterAfterRender({
      filter_type:    parseFilterType(name),
      filter_value:   filterValue,
      item_list_name: _itemListName,
    });
  }, { passive: true });

  // Price filter uses 'input' event — facets.js fires AJAX on 'input' after 300ms.
  // Our debounce (150ms) ensures MutationObserver is set before AJAX fires.
  document.addEventListener('input', (e) => {
    const input = e.target;
    if (input.type !== 'number') return;
    if (!input.name?.includes('price')) return;

    const form = input.closest(FACET_FORMS);
    if (!form) return;

    clearTimeout(_priceDebounce);
    _priceDebounce = setTimeout(() => {
      const gte = form.querySelector('[name$="price.gte"]')?.value || '0';
      const lte = form.querySelector('[name$="price.lte"]')?.value || '';
      fireFilterAfterRender({
        filter_type:    'price',
        filter_value:   lte ? `${gte} - ${lte}` : `${gte}+`,
        item_list_name: _itemListName,
      });
    }, 150);
  }, { passive: true });

  // ─── login & signup ──────────────────────────────────────────────────────────
  // Strategy: set sessionStorage flag on form submit → fire GA4 event on the
  // next page after successful redirect. If form has errors, Shopify re-renders
  // the same URL so the flag is never consumed (path guard below prevents it).

  {
    const path = window.location.pathname;
    const isLoginPage    = path === '/account/login';
    const isRegisterPage = path === '/account/register';

    if (isLoginPage || isRegisterPage) {
      let _pendingMethod = 'email';

      // Detect Google / OTP click before form submit — future-proof hook
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('button, a');
        if (!btn) return;
        const label = (btn.textContent + (btn.getAttribute('aria-label') ?? '')).toLowerCase();
        if (label.includes('google'))       _pendingMethod = 'google';
        else if (label.includes('otp') || label.includes('phone')) _pendingMethod = 'otp';
      }, { passive: true });

      document.addEventListener('submit', () => {
        sessionStorage.setItem('_ga4_auth', JSON.stringify({
          event:  isLoginPage ? 'login' : 'signup',
          method: _pendingMethod,
        }));
        _pendingMethod = 'email'; // reset for next attempt on same page
      });
    }

    // Not on an auth page → check if we just redirected from a successful login/signup
    if (!isLoginPage && !isRegisterPage) {
      const raw = sessionStorage.getItem('_ga4_auth');
      if (raw) {
        sessionStorage.removeItem('_ga4_auth');
        try {
          const { event, method } = JSON.parse(raw);
          onIdle(() => window.dataLayer.push({ event, method }));
        } catch {}
      }
    }
  }

  // ─── pincode_check ───────────────────────────────────────────────────────────

  let _pincodeUserInitiated = false;

  // Flag is set on button click or Enter key — auto-checks on page load stay false
  document.addEventListener('click', (e) => {
    if (e.target.closest('[name="pincode-submit"]')) _pincodeUserInitiated = true;
  }, { passive: true });

  document.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' && e.target.closest('pincode-checker, flexi-pincode-checker')) {
      _pincodeUserInitiated = true;
    }
  }, { passive: true });

  const _origFetch = window.fetch;
  window.fetch = async (...args) => {
    const url = String(args[0] ?? '');
    const res = await _origFetch(...args);

    if (!url.includes('openleaf.tech') || !url.includes('closestTATUnicommerce')) {
      return res;
    }

    const params   = new URL(url).searchParams;
    const pincode  = params.get('pincode') ?? '';
    const sku      = params.get('sku')     ?? '';

    const userInitiated    = _pincodeUserInitiated;
    _pincodeUserInitiated  = false;

    try {
      const data          = await res.clone().json();
      const values        = Array.isArray(data?.values) ? data.values : [];
      const isServiceable = values[2] === 'true' || values[2] === true;

      window.dataLayer.push({
        event:                   'pincode_check',
        pincode,
        is_serviceable:          isServiceable,
        item_id:                 sku,
        estimated_delivery_days: isServiceable ? (parseInt(values[0], 10) || 0) : 0,
        user_initiated:          userInitiated,
      });
    } catch { /* non-pincode fetch errors silently ignored */ }

    return res;
  };

  // ─── select_promotion ────────────────────────────────────────────────────────

  onDOMReady(() => {
    document.querySelectorAll('[data-promo-id]').forEach((link) => {
      link.addEventListener('click', () => {
        const { promoId = '', promoName = '', promoSlot = '', promoLocation = '' } = link.dataset;
        console.log('[GA4] select_promotion', { promoId, promoName, promoSlot, promoLocation });
        dlPushEcom({
          event: 'select_promotion',
          ecommerce: {
            promotion_id:   promoId,
            promotion_name: promoName,
            creative_slot:  promoSlot,
            location_id:    promoLocation,
          },
        });
      });
    });
  });

  // ─── select_item ─────────────────────────────────────────────────────────────

  document.addEventListener('click', (e) => {
    const card = e.target.closest('.product-card.card-wrapper');
    if (!card) return;
    // Notify Me on a sold-out card opens a popup; it isn't selecting the product.
    if (e.target.closest('[data-notify-card-trigger]')) return;

    const {
      listId = '', listName = '', productId = '',
      itemName = '', itemBrand = '', itemCategory = '',
      price = '0', index = '0',
    } = card.dataset;

    dlPushEcom({
      event: 'select_item',
      ecommerce: {
        item_list_id:   listId,
        item_list_name: listName,
        items: [{
          item_id:       productId,
          item_name:     itemName,
          item_brand:    itemBrand,
          item_category: itemCategory,
          price:         (parseInt(price, 10) || 0) / 100,
          index:         parseInt(index, 10) || 0,
        }],
      },
    });
  });

  // ─── notify_me_initiate & notify_me_filled ───────────────────────────────────
  // location: "card" (sold-out product card, shared popup) or "pdp" (product page
  // button, incl. sticky bar). Tracked from outside notify-me.js: initiate on the
  // button click, filled when the stock-notifications POST succeeds.

  let _notifyMeLocation = 'pdp';

  document.addEventListener('click', (e) => {
    const cardBtn = e.target.closest('[data-notify-card-trigger]');
    const pdpBtn  = cardBtn ? null : e.target.closest('ugaoo-notify-me:not([data-shared]) .ugaoo-notify-me__trigger');
    if (!cardBtn && !pdpBtn) return;

    _notifyMeLocation = cardBtn ? 'card' : 'pdp';
    const productId   = cardBtn
      ? cardBtn.dataset.productId
      : pdpBtn.closest('ugaoo-notify-me').dataset.productId;

    window.dataLayer.push({
      event:      'notify_me_initiate',
      location:   _notifyMeLocation,
      product_id: productId ?? '',
    });
  }, { passive: true });

  const _notifyMeFetch = window.fetch;
  window.fetch = async (...args) => {
    const [input, init] = args;
    const url      = String(input?.url ?? input ?? '');
    const location = _notifyMeLocation; // captured before the await
    const res      = await _notifyMeFetch(...args);

    if (!url.includes('/stock-notifications') || String(init?.method).toUpperCase() !== 'POST' || !res.ok) {
      return res;
    }

    try {
      window.dataLayer.push({
        event:      'notify_me_filled',
        location,
        product_id: String(JSON.parse(init.body)?.product_id ?? ''),
      });
    } catch { /* malformed body — skip tracking, never break the request */ }

    return res;
  };

  // ─── cart_upsell_atc (cart drawer recs only) ──────────────────────────────────
  // Scoped to `.csr` (snippets/cart-smart-recommendations.liquid's "bought
  // together" / tier-gap strip) so this only fires for the recs ATC, not the
  // <a class="product-card__atc-btn product-form__submit"> "View Product" link
  // rendered by product-card.liquid elsewhere on the site (that one only
  // navigates, it never adds to cart). Fires optimistically on click, same as
  // select_item/select_promotion above, since <add-to-cart> (assets/global.js)
  // AJAX-adds and there's no separate per-click success signal to hook without
  // diffing the whole cart.

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.csr .product-card__atc-btn.product-form__submit');
    if (!btn) return;

    const card = btn.closest('.product-card.card-wrapper');
    if (!card) return;

    const {
      itemName = '', itemBrand = '', itemCategory = '',
      price = '0', listName = '', listId = '', index = '0',
    } = card.dataset;
    const variantId = btn.dataset.variantId || '';

    dlPushEcom({
      event: 'cart_upsell_atc',
      ecommerce: {
        currency: window.Shopify?.currency?.active || '',
        value:    (parseInt(price, 10) || 0) / 100,
        items: [{
          item_id:        variantId,
          item_name:      itemName,
          item_brand:     itemBrand,
          item_category:  itemCategory,
          price:          (parseInt(price, 10) || 0) / 100,
          quantity:       1,
          index:          parseInt(index, 10) || 0,
          item_list_id:   listId,
          item_list_name: listName,
        }],
      },
    });
  });

  // ─── apply_coupon / remove_coupon / view_coupon_summary ──────────────────────
  // Hooks into the existing coupon widget (snippets/cart-coupon-drawer.liquid +
  // assets/cart-coupon-drawer.js) purely from the outside - no changes made to
  // that component. It relies on three things it already exposes:
  //   - [data-coupon-action] / [data-discount-code] on each Apply/Remove button
  //   - the 'cart:updated' CustomEvent it dispatches (with the fresh cart in
  //     e.detail.cart) only after a successful apply/remove
  //   - the native 'toggle' event on its <details class="mini-cart__coupon-drawer">
  //     ("View all coupons" panel)

  {
    // Captured at click time (apply/remove is still whatever Liquid rendered,
    // since cart-coupon-drawer.js only flips it after a successful re-sync) and
    // consumed by the next 'cart:updated'. 'cart:updated' also fires for
    // unrelated cart mutations (qty changes elsewhere), so a stale pending
    // click is dropped instead of mis-attributed if too much time has passed.
    let _pendingCoupon = null;
    const PENDING_TTL_MS = 5000;

    // Capture phase, not bubble: <coupon-offer-list>'s own click handler
    // (bound directly on the element in its constructor) sits between the
    // button and document on the bubble path and runs first, synchronously
    // adding the 'is-loading' class before a bubble-phase listener here would
    // ever see the click. Capture runs top-down before that, so the button's
    // pre-click state is read reliably.
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.mini-cart__coupon-widget [data-coupon-action]');
      if (!button || button.classList.contains('is-loading')) return;

      _pendingCoupon = {
        action: button.dataset.couponAction === 'remove' ? 'remove' : 'apply',
        code:   button.dataset.discountCode || '',
        ts:     Date.now(),
      };
    }, true);

    document.addEventListener('cart:updated', (e) => {
      if (!_pendingCoupon) return;
      const pending = _pendingCoupon;
      _pendingCoupon = null;
      if (Date.now() - pending.ts > PENDING_TTL_MS) return;

      const cart = e?.detail?.cart;
      dlPushEcom({
        event: pending.action === 'remove' ? 'remove_coupon' : 'apply_coupon',
        ecommerce: {
          coupon:   pending.code,
          currency: cart?.currency ?? '',
          value:    typeof cart?.total_price === 'number' ? cart.total_price / 100 : undefined,
        },
      });
    });
  }

  // 'toggle' does not bubble, so catching it via delegation requires the
  // capture phase rather than the usual bubbling listener used elsewhere here.
  document.addEventListener('toggle', (e) => {
    if (!e.target.matches?.('.mini-cart__coupon-drawer') || !e.target.open) return;
    window.dataLayer.push({ event: 'view_coupon_summary' });
  }, true);

  // ─── collection_slider_view / _card_click / _add_to_cart ─────────────────────
  // sections/collection-section.liquid ("Collection Slider") marks its slider
  // with [data-collection-slider] + heading/id/page type. These are custom
  // (non-ecommerce) events, so they don't clash with select_item above.
  //   view        — once per slider per page load, when ≥50% of it is on screen
  //   card_click  — any product link on a card (image, title, View Product)
  //   add_to_cart — fired after /cart/add succeeds, not on the button click

  const sliderParams = (slider) => ({
    slider_heading: slider.dataset.sliderHeading || '',
    slider_id:      slider.dataset.sliderId || '',
    page_type:      slider.dataset.pageType || '',
    page_path:      window.location.pathname,
  });

  const sliderProductParams = (card) => ({
    product_id:       card.dataset.productId || '',
    product_name:     card.dataset.itemName || '',
    product_url:      new URL(card.dataset.productUrl || '', window.location.origin).href,
    product_price:    (parseInt(card.dataset.price, 10) || 0) / 100,
    product_position: parseInt(card.dataset.index, 10) || 0,
  });

  onDOMReady(() => {
    const sliders = document.querySelectorAll('[data-collection-slider]');
    if (!sliders.length || !('IntersectionObserver' in window)) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        window.dataLayer.push({ event: 'collection_slider_view', ...sliderParams(entry.target) });
      });
    }, { threshold: 0.5 });

    sliders.forEach((slider) => io.observe(slider));
  });

  // Links on the card, plus the rest of the card body, which
  // assets/ugaoo-product-card-sync.js turns into a click-to-navigate area
  // (same exclusions as that handler, so ATC / drawer clicks aren't counted).
  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-collection-slider] .product-card.card-wrapper');
    if (!card) return;

    const link = e.target.closest('a[href]');
    if (!link && e.target.closest('button, form, input, .pcd-drawer')) return;

    window.dataLayer.push({
      event: 'collection_slider_card_click',
      ...sliderParams(card.closest('[data-collection-slider]')),
      ...sliderProductParams(card),
      ...(link && { product_url: link.href }),
    });
  }, { passive: true });

  {
    // <product-form> (assets/global.js) AJAX-adds and then publishes cartUpdate
    // with the variant id; remember which slider card submitted, push on success.
    // A failed fetch publishes neither cartUpdate nor cartError, so a pending
    // add expires instead of being credited to a later add of the same variant.
    const PENDING_ATC_TTL_MS = 10000;
    let pendingSliderAtc = null;

    const pushSliderAtc = ({ slider, card, variantId }) => {
      window.dataLayer.push({
        event: 'collection_slider_add_to_cart',
        ...sliderParams(slider),
        ...sliderProductParams(card),
        variant_id: variantId,
        currency:   window.Shopify?.currency?.active || '',
      });
    };

    document.addEventListener('submit', (e) => {
      const form = e.target;
      const slider = form.closest?.('[data-collection-slider]');
      const card = slider && form.closest('.product-card.card-wrapper');
      if (!card) return;

      const pending = {
        slider,
        card,
        variantId: String(form.querySelector('[name="id"]')?.value || ''),
        ts:        Date.now(),
      };

      // Same bail-out as ProductForm.onSubmitHandler: the form posts natively and
      // the page navigates, so there's no success callback — push now.
      const nativeSubmit = document.body.classList.contains('template-cart')
        || !window.theme?.shopSettings?.cartDrawer;
      if (nativeSubmit || typeof subscribe !== 'function') {
        pushSliderAtc(pending);
        return;
      }
      pendingSliderAtc = pending;
    }, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
        if (!pendingSliderAtc || data?.source !== 'product-form') return;
        if (Date.now() - pendingSliderAtc.ts > PENDING_ATC_TTL_MS) { pendingSliderAtc = null; return; }
        if (String(data.productVariantId) !== pendingSliderAtc.variantId) return;
        pushSliderAtc(pendingSliderAtc);
        pendingSliderAtc = null;
      });
      subscribe(PUB_SUB_EVENTS.cartError, () => { pendingSliderAtc = null; });
    }
  }

  // ─── variant_change ──────────────────────────────────────────────────────────
  // Swatch buttons (.swatch-group) inside <variant-radios>, from
  // snippets/product-variant-picker.liquid (PDP, quick view, featured product).
  // Only real shopper changes (isTrusted): the picker auto-selects other
  // options with synthetic change events, synchronously inside the shopper's
  // change, so by the time this bubbles to document <variant-radios> already
  // holds the final variant. variant_id is '' when the combo doesn't exist.
  //
  // The previous variant is snapshotted in the document capture phase of the
  // radio's click, which runs before the picker's own click handler
  // (product-variant-picker.liquid) auto-selects and updates currentVariant.
  // The change capture is only a fallback for changes with no click. The
  // snapshot is cleared after the task, so a click that changes nothing
  // (already-checked radio) can't leak into a later change.

  {
    const SWATCH_RADIO = '.swatch-group input[type="radio"]';
    const swatchPicker = (e) =>
      e.target.matches?.(SWATCH_RADIO) ? e.target.closest('variant-radios') : null;

    let previousVariant = null;
    let hasSnapshot = false;

    const snapshot = (picker) => {
      if (hasSnapshot) return;
      previousVariant = picker.currentVariant || null;
      hasSnapshot = true;
      setTimeout(() => { previousVariant = null; hasSnapshot = false; }, 0);
    };

    document.addEventListener('click', (e) => {
      const picker = swatchPicker(e);
      if (picker) snapshot(picker);
    }, true);

    document.addEventListener('change', (e) => {
      const picker = e.isTrusted && swatchPicker(e);
      if (picker) snapshot(picker);
    }, true);

    document.addEventListener('change', (e) => {
      const picker = e.isTrusted && swatchPicker(e);
      if (!picker) return;

      const variant = picker.currentVariant || null;
      const previous = previousVariant;
      previousVariant = null;
      hasSnapshot = false;
      if (variant && previous && variant.id === previous.id) return;

      window.dataLayer.push({
        event:                  'variant_change',
        product_id:             picker.dataset.productId || '',
        product_name:           picker.dataset.productTitle || '',
        option_name:            e.target.getAttribute('name') || '',
        option_value:           e.target.value,
        variant_id:             variant ? String(variant.id) : '',
        variant_title:          variant?.title ?? '',
        variant_sku:            variant?.sku ?? '',
        variant_price:          variant ? variant.price / 100 : 0,
        variant_available:      variant ? Boolean(variant.available) : false,
        previous_variant_id:    previous ? String(previous.id) : '',
        previous_variant_title: previous?.title ?? '',
        currency:               window.Shopify?.currency?.active || '',
        page_type:              picker.dataset.pageType || '',
        page_path:              window.location.pathname,
      });
    });
  }

  // ─── promotional_banner_click ────────────────────────────────────────────────
  // snippets/inline-banner-card.liquid — used on collection pages (incl. the
  // coupon page). Delegated so it works for every instance without any Liquid
  // changes.

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.inline-banner-card__btn');
    if (!btn) return;

    const heading = btn.closest('.inline-banner-card')
      ?.querySelector('.inline-banner-card__heading')
      ?.textContent.trim() ?? '';

    const payload = {
      event:          'promotional_banner_click',
      banner_text:    btn.textContent.trim(),
      banner_url:     btn.getAttribute('href') ?? '',
      banner_heading: heading,
      page_location:  window.location.pathname,
    };

    window.dataLayer.push(payload);
  }, { passive: true });

  // ─── corporate gifting ───────────────────────────────────────────────────────
  // Product details come from what Shopify already puts on every PDP
  // (ShopifyAnalytics.meta, og:title, /products/<handle> URL), so these events
  // say which PDP (if any) the lead came from without any Liquid changes.

  const pageContext = () => {
    const meta = window.ShopifyAnalytics?.meta;
    const product = meta?.product;
    const path = window.location.pathname;
    const handle = path.match(/\/products\/([^/?#]+)/)?.[1] ?? '';
    const isProduct = !!(product || handle);
    const name = isProduct
      ? (document.getElementById('b2bProductName')?.value
        || document.querySelector('meta[property="og:title"]')?.content
        || '')
      : '';
    const type = isProduct ? 'product'
      : meta?.page?.pageType || (path === '/' ? 'home' : 'other');
    return {
      page_type:      type,
      page_location:  path,
      product_id:     product?.id ? String(product.id) : '',
      product_name:   name,
      product_handle: handle,
      product_type:   product?.type ?? '',
    };
  };

  // sections/corporate-form-popup.liquid — fired only after a successful submit.
  document.addEventListener('corporate:inquiry-submitted', (e) => {
    const d = e.detail || {};
    window.dataLayer.push({
      event:             'corporate_inquiry',
      form_id:           d.form_id ?? '',
      inquiry_source:    d.inquiry_source ?? '',
      quantity_range:    d.quantity ?? '',
      delivery_location: d.delivery_location ?? '',
      ticket_id:         d.ticket_id ?? '',
      submission_status: d.submission_status ?? '',
      ...pageContext(),
    });
  });

  // snippets/b2b-unlock.liquid — "Customise & Order" on the PDP. The button is
  // disabled until name + phone are valid, and disabled buttons don't fire
  // clicks, so this only counts real attempts.
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#b2bCTA')) return;
    const tier = document.querySelector('.b2b-tier.selected');
    window.dataLayer.push({
      event:          'corporate_customize',
      quantity_range: tier?.dataset.tier ?? '',
      tier_price:     Number(tier?.dataset.price) || undefined,
      sku:            document.getElementById('b2bProductSku')?.value ?? '',
      ...pageContext(),
    });
  }, { passive: true });
})();
